import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles, tokens, Text, Button } from '@fluentui/react-components';
import { Heading, Container } from '../../theme/primitives';
import { iconMap } from '../../theme/icons';
import { stonePalette } from '../../theme/brand';
import meistrasImg from '../../assets/img/marketing/carpenter-visit.jpg';

const CheckIcon = iconMap.check;

const useStyles = makeStyles({
  wrap: {
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: '1.5rem',
    paddingBottom: '1.5rem',
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  // Neutrali "stone" fono spalva — ta pati, kaip HowToChoosePanels.jsx (žr.
  // /admin/bugs "vietoj žalios background spalvos pritaikyk kokia nors kita
  // tinkančia bendrai spalvų paletei"), ne žalias brand tintas.
  card: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    borderRadius: tokens.borderRadiusXLarge,
    overflow: 'hidden',
    backgroundColor: stonePalette[100],
    '@media (min-width: 640px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
  },
  // Didesnis kairysis paddingas (žr. /admin/bugs "pirmoji sekcija turi tureti
  // didesni padding nuo kaires puses") — +15px daugiau (žr. /admin/bugs
  // "Pastumti si turini i desine puse 15px").
  content: {
    paddingTop: tokens.spacingHorizontalXXL, paddingBottom: tokens.spacingHorizontalXXL,
    paddingLeft: '2rem', paddingRight: tokens.spacingHorizontalXXL,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '@media (min-width: 640px)': { paddingTop: '1.5rem', paddingBottom: '1.5rem', paddingLeft: '55px', paddingRight: '1.75rem' },
  },
  title: { marginBottom: tokens.spacingVerticalS },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalL,
    listStyle: 'none',
    padding: 0,
  },
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  checkIcon: { color: tokens.colorBrandForeground2, flexShrink: 0, fontSize: '18px' },
  price: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalM,
  },
  asterisk: { color: tokens.colorPaletteRedForeground1 },
  priceValue: { fontWeight: tokens.fontWeightSemibold, marginLeft: tokens.spacingHorizontalXS },
  // Standartinis Fluent <Button> — tik alignSelf, kad neišsitemptų per visą
  // flex stulpelio plotį (žr. 18 punktų sąrašo #5, anksčiau čia buvo rankomis
  // rašytas <Link>, kuris dubliavo Button stilių su kitokiu spinduliu).
  cta: { alignSelf: 'flex-start' },
  footnote: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalS,
  },
  imageWrap: { minHeight: '110px', '@media (min-width: 640px)': { minHeight: '150px' } },
  image: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
});

const CarpenterBanner = () => {
  const s = useStyles();
  const { t } = useTranslation();
  return (
    <Container size="xl" className={s.wrap}>
      <div className={s.card}>
        <div className={s.content}>
          <Heading as="h2" variant="h2" className={s.title}>{t('shop.carpenter_banner.title')}</Heading>
          <ul className={s.list}>
            {[
              t('shop.carpenter_banner.item_0'),
              t('shop.carpenter_banner.item_1'),
              t('shop.carpenter_banner.item_2'),
            ].map(text => (
              <li key={text} className={s.listItem}>
                <CheckIcon className={s.checkIcon} />
                {text}
              </li>
            ))}
          </ul>
          <Text as="p" className={s.price}>
            {t('shop.carpenter_banner.price_label')}<span className={s.asterisk}>*</span>: <span className={s.priceValue}>{t('shop.carpenter_banner.price_value')}</span>
          </Text>
          <Button appearance="primary" size="large" as={Link} to="/meistro-uzsakymas" className={s.cta}>
            {t('shop.carpenter_banner.cta')}
          </Button>
          <Text as="p" className={s.footnote}>
            <span className={s.asterisk}>*</span> {t('shop.carpenter_banner.footnote')}
          </Text>
        </div>
        <div className={s.imageWrap}>
          <img src={meistrasImg} alt={t('shop.carpenter_banner.img_alt')} className={s.image} />
        </div>
      </div>
    </Container>
  );
};

export default CarpenterBanner;
